import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageSEO from '@/components/PageSEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Clock, ArrowLeft, ArrowRight, User, BookOpen } from 'lucide-react';
import { getPostBySlug, getAllPosts } from '@/data/blog/posts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const allPosts = getAllPosts();

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Find related posts (same category, excluding current)
  const relatedPosts = allPosts
    .filter(p => p.category === post.category && p.slug !== post.slug)
    .slice(0, 2);

  // Find next/prev posts
  const currentIndex = allPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Article schema for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": `https://comalglass.com${post.image}`,
    "author": {
      "@type": "Organization",
      "name": "Comal Glass Company"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Comal Glass Company",
      "logo": {
        "@type": "ImageObject",
        "url": "https://comalglass.com/images/logos/GLASS.jpg"
      }
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://comalglass.com/blog/${post.slug}`
    }
  };

  return (
    <div className="min-h-screen">
      <PageSEO 
        title={`${post.title} | The Glass Spec`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900" />
        <div className="container-custom relative z-10">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to The Glass Spec
            </Link>
          </div>

          <div className="max-w-4xl">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
              {post.category}
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-white leading-tight">
              {post.title}
            </h1>
            <p className="text-xl text-zinc-300 mb-6">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-6 text-zinc-400">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="relative -mt-8 mb-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-xl overflow-hidden shadow-2xl">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-64 sm:h-80 lg:h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <article className="max-w-none">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({node, ...props}) => <h2 className="text-2xl font-bold mt-10 mb-6 pb-2 border-b border-border text-foreground" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-bold mt-8 mb-4 text-foreground" {...props} />,
                  h4: ({node, ...props}) => <h4 className="text-lg font-semibold mt-6 mb-3 text-foreground" {...props} />,
                  p: ({node, ...props}) => <p className="text-muted-foreground leading-relaxed mb-6" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc list-inside text-muted-foreground my-6 space-y-2 ml-4" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-inside text-muted-foreground my-6 space-y-2 ml-4" {...props} />,
                  li: ({node, ...props}) => <li className="text-muted-foreground" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-foreground" {...props} />,
                  a: ({node, ...props}) => <a className="text-primary hover:underline" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-primary bg-muted/50 py-2 px-6 rounded-r my-6 italic" {...props} />,
                  table: ({node, ...props}) => <div className="overflow-x-auto my-8"><table className="w-full border-collapse" {...props} /></div>,
                  th: ({node, ...props}) => <th className="bg-muted p-4 text-left font-semibold border border-border" {...props} />,
                  td: ({node, ...props}) => <td className="p-4 border border-border" {...props} />,
                  hr: ({node, ...props}) => <hr className="my-8 border-border" {...props} />,
                }}
              >
                {post.content.trim()}
              </ReactMarkdown>
            </article>

            {/* CTA Box */}
            <div className="mt-12 p-8 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-xl border border-primary/20">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">Ready to Start Your Project?</h3>
                <p className="text-muted-foreground mb-4">
                  Contact us for a free consultation and quote. Installation can happen in as little as one week.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button className="bg-primary hover:bg-primary/90" asChild>
                    <Link to="/contact">Get a Free Quote</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="tel:8303599925">(830) 359-9925</a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Post Navigation */}
            <div className="mt-12 pt-8 border-t">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                {prevPost ? (
                  <Link 
                    to={`/blog/${prevPost.slug}`}
                    className="group flex-1 p-4 rounded-lg border hover:border-primary/50 hover:bg-muted/50 transition-all"
                  >
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                      <ArrowLeft className="w-3 h-3" /> Previous Article
                    </span>
                    <span className="font-medium group-hover:text-primary transition-colors line-clamp-1">
                      {prevPost.title}
                    </span>
                  </Link>
                ) : <div className="flex-1" />}
                
                {nextPost ? (
                  <Link 
                    to={`/blog/${nextPost.slug}`}
                    className="group flex-1 p-4 rounded-lg border hover:border-primary/50 hover:bg-muted/50 transition-all text-right"
                  >
                    <span className="text-xs text-muted-foreground flex items-center gap-1 justify-end mb-1">
                      Next Article <ArrowRight className="w-3 h-3" />
                    </span>
                    <span className="font-medium group-hover:text-primary transition-colors line-clamp-1">
                      {nextPost.title}
                    </span>
                  </Link>
                ) : <div className="flex-1" />}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gradient-subtle">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link key={relatedPost.slug} to={`/blog/${relatedPost.slug}`} className="group">
                    <Card className="overflow-hidden border-none shadow-md hover:shadow-lg transition-all h-full">
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-bold group-hover:text-primary transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage;
